import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Loader2, Save, KeyRound, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const CLAUDE_MODELS = [
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', description: 'Fastest Claude option' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', description: 'Best default for translation and call QA' },
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6', description: 'Deepest review for complex calls' },
];

export default function SettingsPage() {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      api.getSettings()
        .then(setSettings)
        .catch(() => setSettings({
          polling_interval_minutes: 5,
          batch_size: 5,
          max_concurrent_processing: 3,
          ai_provider: 'claude',
          transcription_engine: 'soniox',
          soniox_model: 'stt-async-v4',
          sarvam_model: 'saaras:v3',
          anthropic_model: 'claude-sonnet-4-6',
          has_anthropic_api_key: false,
        }))
        .finally(() => setLoading(false));

      api.getUsers().then(setUsers).catch(() => {});
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const handleChangePassword = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    if (!newPassword || newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await api.resetPassword(selectedUserId, newPassword);
      toast.success('Password changed successfully');
      setSelectedUserId('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...settings,
        ai_provider: 'claude',
        transcription_engine: 'soniox',
      };
      const updatedSettings = await api.updateSettings(payload);
      setSettings(updatedSettings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return <div className="text-center py-12 text-muted-foreground">Admin access required</div>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Application configuration</p>
      </div>

      {/* Application Settings */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-6">
        <div>
          <h3 className="text-sm font-semibold mb-4">Polling Configuration</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Polling Interval (minutes)</label>
              <Input
                type="number"
                min={1}
                max={60}
                value={settings?.polling_interval_minutes || 5}
                onChange={(e) => setSettings({ ...settings, polling_interval_minutes: parseInt(e.target.value) })}
                className="bg-background"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Batch Processing Size</label>
              <Input
                type="number"
                min={1}
                max={20}
                value={settings?.batch_size || 5}
                onChange={(e) => setSettings({ ...settings, batch_size: parseInt(e.target.value) })}
                className="bg-background"
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-4">Processing Configuration</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Max Concurrent Processing</label>
              <Input
                type="number"
                min={1}
                max={10}
                value={settings?.max_concurrent_processing || 3}
                onChange={(e) => setSettings({ ...settings, max_concurrent_processing: parseInt(e.target.value) })}
                className="bg-background"
              />
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-4">AI Configuration</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">AI Provider</label>
              <Input value="Claude" disabled className="bg-background" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Claude Model</label>
              <select
                value={settings?.anthropic_model || 'claude-sonnet-4-6'}
                onChange={(e) => setSettings({ ...settings, anthropic_model: e.target.value })}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
              >
                {CLAUDE_MODELS.map((model) => (
                  <option key={model.value} value={model.value}>
                    {model.label} ({model.description})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 p-4 border border-border rounded-lg space-y-4">
            <h4 className="text-sm font-semibold">Transcription Engine and Model</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Transcription Engine</label>
                <Input value="Soniox" disabled className="bg-background" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Soniox Model</label>
                <select
                  value={settings?.soniox_model || 'stt-async-v4'}
                  onChange={(e) => setSettings({ ...settings, soniox_model: e.target.value })}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="stt-async-v4">STT Async V4</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-border">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>

      {/* Change Password Section - Admin only */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Change Password</h3>
        </div>
        <p className="text-xs text-muted-foreground">Reset password for any user (agents, supervisors, or admins)</p>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Select User</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">-- Select a user --</option>
              {[...users]
                .sort((a, b) =>
                  (a.name || a.agent_name || a.email || '').localeCompare(
                    b.name || b.agent_name || b.email || ''
                  )
                )
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name || user.agent_name || user.email} ({user.role})
                  </option>
                ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">New Password</label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="bg-background pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Confirm Password</label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="bg-background pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div>
            <Button onClick={handleChangePassword} disabled={changingPassword} variant="outline">
              {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Change Password
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
